// ============================================
// TIPOCAMBIOCONFIG.JS — Pestana propia "Tipo de cambio"
// ============================================
// W1.1-B: el TC aplica a TODOS los productos (Syscom y Beep), por eso vive
// en su propia pestana y no dentro de "Conexion Syscom". Muestra el TC de
// mercado (vivo), permite EDITAR el margen de proteccion y ver el TC de venta
// resultante (mercado + margen) en vivo. Persiste el margen en cotizador_config
// (clave margen_tc_proteccion) via PATCH /api/cotizador/config.

import React, { useCallback, useEffect, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
} from './components/helpersClientes';
import { authHeaders } from './utils/storage';

function formatTC(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function formatHora(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return '—'; }
}

export default function TipoCambioConfig() {
  const [tcInfo, setTcInfo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [margen, setMargen] = useState('');      // texto editable
  const [guardando, setGuardando] = useState(false);
  const [actualizando, setActualizando] = useState(false);
  const [toast, setToast] = useState(null);

  const mostrarToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2800); };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/tc/actual`).then(x => x.json());
      if (r.ok) {
        setTcInfo(r);
        if (r.margen_proteccion != null) setMargen(String(r.margen_proteccion));
      }
    } catch (e) {
      mostrarToast('⚠️ ' + e.message);
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // TC de venta en vivo: mercado + margen (lo que el usuario esta escribiendo).
  const tcMercado = tcInfo?.tc_mercado != null ? Number(tcInfo.tc_mercado) : null;
  const margenNum = Number(margen);
  const margenValido = margen !== '' && Number.isFinite(margenNum) && margenNum >= 0;
  const tcVentaVivo = (tcMercado != null && margenValido) ? tcMercado + margenNum : null;
  const margenGuardado = tcInfo?.margen_proteccion != null ? Number(tcInfo.margen_proteccion) : null;
  const sucio = margenValido && margenGuardado != null && margenNum !== margenGuardado;

  const guardar = async () => {
    if (!margenValido) { mostrarToast('⚠️ Margen invalido (usa 0 o mayor)'); return; }
    setGuardando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/cotizador/config`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ margen_tc_proteccion: margenNum }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      mostrarToast('✅ Margen guardado');
      await cargar();
    } catch (e) {
      mostrarToast('⚠️ ' + e.message);
    } finally { setGuardando(false); }
  };

  const actualizarTC = async () => {
    setActualizando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/tc/actualizar`, { method: 'POST', headers: { ...authHeaders() } }).then(x => x.json());
      if (r.ok) mostrarToast(`✅ TC de mercado actualizado: $${formatTC(r.tc_fix)}`);
      else mostrarToast('⚠️ ' + (r.error || 'El mercado no respondio'));
      await cargar();
    } catch (e) {
      mostrarToast('⚠️ ' + e.message);
    } finally { setActualizando(false); }
  };

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, padding: 20, background: COLOR_FONDO, minHeight: '100%' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>💱 Tipo de cambio</div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4, lineHeight: 1.5 }}>
          Aplica a TODOS los productos (Syscom y Beep). TC de venta = mercado + margen de proteccion.
        </div>
      </div>

      {cargando && <div style={{ color: COLOR_GRIS_CLARO }}>Cargando…</div>}

      {!cargando && (
        <div style={{ ...estiloTarjeta, padding: 20, marginBottom: 14, maxWidth: 560 }}>
          {/* TC de mercado (vivo) */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              TC de mercado (USD → MXN)
            </div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>
              {tcInfo?.fuente ? `Fuente: ${tcInfo.fuente} · ` : ''}consultado {formatHora(tcInfo?.consultado_en)}
            </div>
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: COLOR_OSCURO }}>
            ${formatTC(tcMercado)} <span style={{ fontSize: 13, fontWeight: 600, color: COLOR_GRIS_CLARO }}>MXN</span>
          </div>
          {tcInfo?.warning && (
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>⚠ {tcInfo.warning}</div>
          )}

          <div style={{ borderTop: `1px solid ${COLOR_BORDE}`, margin: '16px 0' }} />

          {/* Margen editable */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 14, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>Margen de proteccion</div>
              <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2, lineHeight: 1.4 }}>
                Colchon en MXN sobre el TC de mercado. Acepta 0, 1, 2, 1.5…
              </div>
            </div>
            <div style={{ position: 'relative' }}>
              <input
                style={{ ...estiloInput, paddingRight: 48, textAlign: 'right', fontSize: 16, fontWeight: 700 }}
                type="number" step="0.01" min="0" inputMode="decimal"
                value={margen}
                onChange={(e) => setMargen(e.target.value)}
              />
              <span style={{ position: 'absolute', right: 12, top: 11, color: COLOR_GRIS_CLARO, fontSize: 12 }}>MXN</span>
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${COLOR_BORDE}`, margin: '16px 0' }} />

          {/* TC de venta en vivo */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              TC de venta (aplicado)
            </div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>
              mercado ${formatTC(tcMercado)} + margen ${margenValido ? formatTC(margenNum) : '—'}
            </div>
          </div>
          <div style={{ fontSize: 30, fontWeight: 800, color: '#1D9E75' }}>
            ${formatTC(tcVentaVivo)} <span style={{ fontSize: 13, fontWeight: 600, color: COLOR_GRIS_CLARO }}>MXN/USD</span>
          </div>

          {/* Acciones */}
          <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
            <button
              style={{ ...estiloBotonPrimario, background: '#1D9E75', opacity: (!sucio || guardando) ? 0.5 : 1 }}
              disabled={!sucio || guardando}
              onClick={guardar}
            >{guardando ? 'Guardando…' : '💾 Guardar margen'}</button>
            <button
              style={{ ...estiloBotonSecundario, opacity: actualizando ? 0.6 : 1 }}
              disabled={actualizando}
              onClick={actualizarTC}
            >{actualizando ? 'Consultando…' : '↻ Actualizar TC ahora'}</button>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff', padding: '10px 16px', borderRadius: 10,
          fontSize: 12, zIndex: 1500, fontFamily: PP,
        }}>{toast}</div>
      )}
    </div>
  );
}
